import React from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useTheme } from '@mui/material/styles';

import FairExText from '../../assets/textImage/fairex-text.svg';
import FairGenText from '../../assets/textImage/fairgen-text.svg';
import CanvasText from '../../assets/icons/canvasIcons/canvasFx.svg';
import CustomEditIcon from '../../assets/icons/canvasIcons/CanvasEditIcon';

import {
  Dashboard as DashboardIcon,
  Payment as PaymentIcon,
  ShowChart as ShowChartIcon,
  Logout as LogoutIcon,
  Forum as ForumIcon,
  Help as HelpIcon,
  Twitter as TwitterIcon,
  AccountCircle as AccountCircleIcon
} from '@mui/icons-material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PhotoSizeSelectLargeIcon from '@mui/icons-material/PhotoSizeSelectLarge';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import { ListItemIcon, ListItemText, Theme } from '@mui/material';
import { Box, styled } from '@mui/system';
import { getActiveProfile } from '../context/AppContext';
import { Link, useNavigate } from 'react-router-dom';
import { ROUTES } from '../../constants/routes';
import { handleSignOut } from '../../handlers/signOut';
import { RootState } from '../../store/store';
import { useSelector } from 'react-redux';

interface NestedMenuProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
  section: 'app' | 'profile';
}

const StyledMenuContent = styled(DropdownMenu.Content)<{
  theme: Theme;
  section: string;
}>(({ theme }) => ({
  minWidth: '160px',
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  boxShadow: theme.shadows[4],
  borderRadius: theme.shape.borderRadius,
  position: 'relative'
}));

const StyledMenuItem = styled(DropdownMenu.Item)<{ theme: Theme }>(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '8px 16px',
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  '&:hover': {
    backgroundColor: theme.palette.action.hover
  },
  '&:focus': {
    outline: 'none'
  },
  cursor: 'pointer'
}));

const StyledSubTrigger = styled(DropdownMenu.SubTrigger)<{ theme: Theme }>(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '8px 16px',
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  '&:hover': {
    backgroundColor: theme.palette.action.hover
  },
  '&:focus': {
    outline: 'none'
  }
}));

const StyledSubContent = styled(DropdownMenu.SubContent)<{ theme: Theme }>(({ theme }) => ({
  minWidth: '150px',
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  boxShadow: theme.shadows[4],
  borderRadius: theme.shape.borderRadius
}));


const Arrow = styled(DropdownMenu.Arrow)<{ theme: Theme }>(({ theme }) => ({
  height: 16,
  width: 24,
  fill: theme.palette.background.paper
}));


const HeaderMenu: React.FC<NestedMenuProps> = ({ open, onOpenChange, children, section }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const activeProfile = getActiveProfile();
  const { profile } = useSelector((state: RootState) => state.profile);

  const enableSwitch = profile ? Object.values(profile).filter(obj => obj.accountType !== undefined).length > 1 : false;

  const obj = {
    profile: {
      setup: true,
      uaseg: false,
      pricing: false,
      switch: enableSwitch
    }
  };

  const availableModes = [];
  if (profile?.artist?.accountType) availableModes.push('artist');
  if (profile?.creator?.accountType) availableModes.push('creator');
  if (profile?.influencer?.accountType) availableModes.push('influencer');

  return (
    <>
      <DropdownMenu.Root modal={false} open={open} onOpenChange={onOpenChange}>
        <DropdownMenu.Trigger asChild>{children}</DropdownMenu.Trigger>
        {section === 'app' ? (
          <StyledMenuContent theme={theme} sideOffset={5} side="bottom" align="start" section={section}>
            <Arrow theme={theme} />
            {activeProfile == 'creator' && (
              <DropdownMenu.Sub>
                <StyledSubTrigger
                  theme={theme}
                  onClick={() => navigate(`/${activeProfile}/dashboard#generated-media`)}
                  sx={{
                    cursor: 'pointer'
                  }}
                >
                  <ListItemIcon>
                    <DashboardIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Dashboard" />
                </StyledSubTrigger>
              </DropdownMenu.Sub>
            )}

            {activeProfile == 'artist' && (
              <>
                <StyledMenuItem theme={theme} onClick={() => navigate('/artist/dashboard#my-art-styles')}>
                  <ListItemIcon>
                    <DashboardIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Dashboard" />
                </StyledMenuItem>
              </>
            )}

            {activeProfile == 'influencer' && (
              <>
                <StyledMenuItem theme={theme} onClick={() => navigate('/influencer/dashboard#portfolio')}>
                  <ListItemIcon>
                    <DashboardIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Dashboard" />
                </StyledMenuItem>
              </>
            )}

            {activeProfile == 'creator' && (
              <StyledMenuItem theme={theme} onClick={() => navigate('/creator/marketplace')}>
                <ListItemIcon>
                  <StorefrontIcon fontSize="small" />
                </ListItemIcon>
                <Box>
                  <Box
                    component="img"
                    src={FairExText}
                    alt={'fairGeb'}
                    sx={{
                      width: 43,
                      height: 16
                    }}
                  />
                  <ListItemText
                    primary="Marketplace"
                    primaryTypographyProps={{
                      sx: {
                        fontFamily: 'Inter',
                        fontWeight: 400,
                        fontSize: '10px',
                        lineHeight: '100%',
                        letterSpacing: '0px',
                        verticalAlign: 'middle'
                      }
                    }}
                  />
                </Box>
              </StyledMenuItem>
            )}

            <StyledMenuItem theme={theme} onClick={() => navigate(ROUTES.FAIRGEN_AI_STUDIO)}>
              <ListItemIcon>
                <PhotoSizeSelectLargeIcon fontSize="small" />
              </ListItemIcon>

              <Box>
                <Box
                  component="img"
                  src={FairGenText}
                  alt={'fairGeb'}
                  sx={{
                    width: 52,
                    height: 16
                  }}
                />

                <ListItemText
                  primary="AI Studio"
                  primaryTypographyProps={{
                    sx: {
                      fontFamily: 'Inter',
                      fontWeight: 400,
                      fontSize: '10px',
                      lineHeight: '100%',
                      letterSpacing: '0px',
                      verticalAlign: 'middle'
                    }
                  }}
                />
              </Box>
            </StyledMenuItem>

            <StyledMenuItem theme={theme} onClick={() => navigate('/canvas-ai-editor')}>
              <ListItemIcon>
                <CustomEditIcon style={{ width: 16, height: 16 }} />
              </ListItemIcon>
              <Box>
                <Box
                  component="img"
                  src={CanvasText}
                  alt={'fairGeb'}
                  sx={{
                    width: 52,
                    height: 16
                  }}
                />
                <ListItemText
                  primary="AI Editor"
                  primaryTypographyProps={{
                    sx: {
                      fontFamily: 'Inter',
                      fontWeight: 400,
                      fontSize: '10px',
                      lineHeight: '100%',
                      letterSpacing: '0px',
                      verticalAlign: 'middle'
                    }
                  }}
                />
              </Box>
            </StyledMenuItem>

            <DropdownMenu.Sub>
              <StyledSubTrigger theme={theme}>
                <ListItemIcon>
                  <HelpIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Help" />
              </StyledSubTrigger>
              <DropdownMenu.Portal>
                <StyledSubContent theme={theme}>
                  <StyledMenuItem theme={theme}>
                    <ListItemIcon>
                      <HelpIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Link to="/faq" target="_blank" rel="noopener noreferrer">
                          FAQs
                        </Link>
                      }
                    />
                  </StyledMenuItem>
                  <StyledMenuItem theme={theme}>
                    <ListItemIcon>
                      <ForumIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Link to="https://t.co/401XPwdXQx" target="_blank" rel="noopener noreferrer">
                          Join Discord
                        </Link>
                      }
                    />
                  </StyledMenuItem>
                  <StyledMenuItem theme={theme}>
                    <ListItemIcon>
                      <TwitterIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Link to="https://bit.ly/XSaagasAI" target="_blank" rel="noopener noreferrer">
                          Follow on X
                        </Link>
                      }
                    />
                  </StyledMenuItem>
                </StyledSubContent>
              </DropdownMenu.Portal>
            </DropdownMenu.Sub>
          </StyledMenuContent>
        ) : section === 'profile' ? (
          <StyledMenuContent theme={theme} sideOffset={5} side="bottom" align="end" section={section}>
            <Arrow theme={theme} />
            <DropdownMenu.Sub>
              <StyledSubTrigger theme={theme}>
                <ListItemIcon>
                  <AccountCircleIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Account" sx={{ cursor: 'pointer' }} onClick={() => navigate('/account')} />
              </StyledSubTrigger>
              <DropdownMenu.Portal>
                <StyledSubContent theme={theme}>
                  {obj.profile.pricing && (
                    <StyledMenuItem theme={theme}>
                      <ListItemIcon>
                        <PaymentIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="Billing" />
                    </StyledMenuItem>
                  )}
                  {obj.profile.uaseg && (
                    <StyledMenuItem theme={theme}>
                      <ListItemIcon>
                        <ShowChartIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="Usage" />
                    </StyledMenuItem>
                  )}
                </StyledSubContent>
              </DropdownMenu.Portal>
            </DropdownMenu.Sub>
            {obj.profile.pricing && (
              <StyledMenuItem theme={theme}>
                <ListItemIcon>
                  <PaidOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Pricing" />
              </StyledMenuItem>
            )}
            <StyledMenuItem theme={theme} onSelect={handleSignOut}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Logout" />
            </StyledMenuItem>
          </StyledMenuContent>
        ) : null}
      </DropdownMenu.Root>
    </>
  );
};

export default HeaderMenu;
